// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Clickable, { ClickablePresentationProps, ClickableProps } from "@insite/mobius/Clickable";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { transitionHoverStyles } from "@store/Themes/common/mixins/transition";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { focusStyles, spacingStyles } from "@toolkit/Themes/common/mixins";
import Icon, { IconPresentationProps } from "@store/MobiusOverrides/Icon/Icon";
import React, { FC } from "react";
import { css } from "styled-components";

export interface NavigationDrawerLinkPresentationProps extends ExpandStyles<NavigationDrawerLinkStyles> {
    /** Optional leading icon source. */
    iconSrc?: IconPresentationProps["src"];
    /** Optional trailing icon source (e.g. a chevron or arrow). */
    trailingIconSrc?: IconPresentationProps["src"];
}

export interface NavigationDrawerLinkProps extends NavigationDrawerLinkPresentationProps {
    /** Applies the active ("on") state styling, e.g. when the link points to the current page. */
    isCurrent?: boolean;
    /** Optional secondary line rendered under the primary label. */
    subText?: string;
    /** Primary label of the row. */
    text?: string;
}

export interface NavigationDrawerLinkStyles {
    icon?: IconPresentationProps;
    link?: ClickablePresentationProps;
    subText?: TypographyPresentationProps;
    text?: TypographyPresentationProps;
    textWrapper?: InjectableCss;
    trailingIcon?: IconPresentationProps;
}

export const navigationDrawerLinkStyles: NavigationDrawerLinkStyles = {
    link: {
        css: css`
            ${focusStyles()}
            ${spacingStyles(pxToRem(4), null)}
            display: flex;
            align-items: center;
            gap: ${pxToRem(12)};
            width: 100%;
            padding: 0.5em ${pxToRem(20)};
            color: ${getColor("text.main")};
            font-weight: ${x => x.theme.design.typography.weight.medium};
            line-height: ${x => x.theme.design.typography.lineHeight.semi};
            transition: ${transitionHoverStyles("color")};

            &:hover,
            &.on {
                color: ${getColor("primary")};
            }
        `,
    },
    icon: {
        size: 24,
        color: "primary",
        css: css`
            flex-shrink: 0;
        `,
    },
    textWrapper: {
        css: css`
            display: flex;
            flex: 1;
            flex-direction: column;
            min-width: 0;
        `,
    },
    subText: {
        css: css`
            min-width: 0;
            max-width: 100%;
            overflow: hidden;
            font-weight: ${x => x.theme.design.typography.weight.base};
            white-space: nowrap;
            text-overflow: ellipsis;
        `,
    },
    trailingIcon: {
        size: 20,
        color: "inherit",
        css: css`
            flex-shrink: 0;
        `,
    },
};

const NavigationDrawerLink: FC<NavigationDrawerLinkProps & ClickableProps> = ({
    className: baseClassName,
    extendedStyles,
    iconSrc: baseIconSrc,
    isCurrent,
    overridedStyles,
    subText,
    text,
    trailingIconSrc: baseTrailingIconSrc,
    ...clickableProps
}) => {
    const styles = useExpandProps(navigationDrawerLinkStyles, extendedStyles, overridedStyles);
    const className = [baseClassName, isCurrent && "on"].filter(Boolean).join(" ") || undefined;
    const iconSrc = baseIconSrc ?? styles.icon?.src;
    const trailingIconSrc = baseTrailingIconSrc ?? styles.trailingIcon?.src;

    return (
        <Clickable {...styles.link} {...clickableProps} className={className}>
            {iconSrc && <Icon {...styles.icon} src={iconSrc} />}
            <StyledWrapper as="span" {...styles.textWrapper}>
                <Typography {...styles.text}>{text}</Typography>
                {subText && <Typography {...styles.subText}>{subText}</Typography>}
            </StyledWrapper>
            {trailingIconSrc && <Icon {...styles.trailingIcon} src={trailingIconSrc} />}
        </Clickable>
    );
};

export default NavigationDrawerLink;
